/**
 * Alerts API Schemas
 *
 * Schemas for alert rules, events, and incidents.
 */

import { z } from 'zod/v4';
import { recordCriterionEvidenceSchema } from './call-criteria';

// ============================================================================
// Enums
// ============================================================================

/**
 * Alert severity level
 *
 * Determines urgency and notification priority:
 * - `low`: Minor issues, informational alerts (e.g., quality score dip below 80%)
 * - `medium`: Notable issues requiring attention (e.g., quality score below 70%, compliance warning)
 * - `high`: Significant issues requiring prompt action (e.g., quality score below 60%, compliance violation)
 * - `critical`: Urgent issues requiring immediate action (e.g., quality score below 50%, severe compliance breach)
 *
 * Severity affects notification channels and escalation policies.
 */
export const alertSeveritySchema = z.enum(['low', 'medium', 'high', 'critical']);

/**
 * Alert event status
 *
 * Tracks alert lifecycle with state transitions:
 * - `active`: Alert triggered, awaiting acknowledgment (initial state)
 * - `acknowledged`: Alert seen by user, investigation in progress
 * - `resolved`: Alert addressed, issue fixed (terminal state)
 *
 * State flow: active → acknowledged → resolved
 * Note: Alerts can be resolved directly from active state if automated resolution occurs.
 */
export const alertStatusSchema = z.enum(['active', 'acknowledged', 'resolved']);

/**
 * Incident status
 *
 * Tracks incident resolution workflow with state transitions:
 * - `open`: Incident created, not yet assigned (initial state)
 * - `in_progress`: Incident assigned, actively being investigated/resolved
 * - `resolved`: Root cause addressed, awaiting verification
 * - `closed`: Incident verified and archived (terminal state)
 *
 * State flow: open → in_progress → resolved → closed
 * Note: Incidents can be reopened by transitioning from resolved back to in_progress.
 */
export const incidentStatusSchema = z.enum(['open', 'in_progress', 'resolved', 'closed']);

// ============================================================================
// Core Schemas
// ============================================================================

/**
 * Alert rule category
 *
 * Classifies alert rules by functional domain:
 * - `quality`: Quality score thresholds (e.g., score < 70%, metrics below target)
 * - `compliance`: Regulatory/policy violations (e.g., missing recording disclaimer, TCPA breach)
 * - `sales`: Sales performance indicators (e.g., low BANT qualification, weak objection handling)
 * - `sentiment`: Customer sentiment issues (e.g., negative sentiment score, escalation detected)
 * - `custom`: User-defined business rules (e.g., custom JSONLogic conditions)
 *
 * Category affects alert routing, dashboard grouping, and reporting.
 */
export const alertCategorySchema = z.enum([
  'quality',
  'compliance',
  'sales',
  'sentiment',
  'custom',
]);

/**
 * Alert rule definition
 * Matches API response from GET /api/v1/alerts/rules
 */
export const alertRuleSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  category: alertCategorySchema,
  severity: alertSeveritySchema,
  conditionJsonlogic: z.unknown().optional(), // JSONLogic rule object
  isEnabled: z.boolean(),
  dedupKeyTemplate: z.string().optional().nullable(),
  throttleSeconds: z.number().optional().nullable(),
  metadata: z.unknown().optional().nullable(),
  presetKey: z.string().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  // How many events this rule has fired (computed at read from events).
  triggeredCount: z.number().optional(),
  // When this rule last fired, or null/absent if it never has.
  lastTriggeredAt: z.string().optional().nullable(),
  // Paths in conditionJsonlogic that don't resolve in the current analysis
  // output catalog — empty when the rule is healthy. See Task 3/5,
  // alert-rules-paths-migration: the API rejects a NEW rule naming one of
  // these; this is how an EXISTING one is surfaced instead of just logging
  // a warn nobody reads.
  unresolvedPaths: z.array(z.string()).optional(),
});

/**
 * Evidence item within an alert event.
 * Transformed by edge-api from raw DB JSON into a display-ready format.
 */
export const alertEvidenceItemSchema = z.object({
  quote: z.string(),
  timecode: z.string().optional(),
  speaker: z.enum(['agent', 'client', 'system']).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/**
 * What the alert card shows for ONE criterion its rule read: the criterion's
 * own quote(s), or — `quotes` empty — nothing to quote ("the event was not
 * found": what the criterion looks for is not in the call, or the answer has
 * no evidence).
 */
export const alertCriterionEvidenceSchema = z.object({
  criterionId: z.string(),
  name: z.string(),
  // The call panel's criterion evidence itself, not a copy: the card reads its
  // quotes from the same answer rows, so the two must not drift apart.
  quotes: z.array(recordCriterionEvidenceSchema),
});

/**
 * `GET /alerts/:id/criterion-evidence` — resolved at READ time, for the in-app
 * card only. A criterion the viewer may not see (no call access, flag off,
 * hidden built-in) is simply absent from `criteria`; the alert's stored event
 * and every notification carry no quote at all.
 */
export const alertCriterionEvidenceResponseSchema = z.object({
  success: z.literal(true),
  criteria: z.array(alertCriterionEvidenceSchema),
});

/**
 * The key moment a raised attention flag stands on (#2002) — the shape
 * `attentionFlagMoment` (`@aica/core`) gives the analysis card. `quote` is
 * empty when PII minimisation removed it; `timestamp` is the model's timecode
 * and may not parse.
 */
export const alertAttentionMomentSchema = z.object({
  timestamp: z.string().nullable().optional(),
  label: z.string(),
  quote: z.string(),
  importance: z.enum(['critical', 'high', 'medium', 'low']).optional(),
  speaker: z.enum(['agent', 'customer', 'unknown']).optional(),
});

/**
 * `GET /alerts/:id/attention-moment` — resolved at READ time, for the in-app
 * card only: the moment is the call's text, so it is neither stored on the
 * alert event nor sent with a notification. `null` covers every "nothing to
 * show" alike — the alert does not stand on the flag's reason, the analysis
 * marks no single moment, or the viewer may not read the call.
 */
export const alertAttentionMomentResponseSchema = z.object({
  success: z.literal(true),
  moment: alertAttentionMomentSchema.nullable(),
});

/**
 * Alert event (triggered by rule)
 */
export const alertEventSchema = z.object({
  id: z.string(),
  ruleId: z.string(),
  recordId: z.string().optional().nullable(),
  projectId: z.string(),
  analysisId: z.string().optional().nullable(),
  severity: alertSeveritySchema,
  status: alertStatusSchema,
  message: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  ruleName: z.string().optional(),
  rulePresetKey: z.string().optional().nullable(),
  /**
   * Human-readable label for the record this event fired on (agent or
   * participant name). Null when the record has neither, or was deleted.
   * UI falls back to the hex record-ID fragment when this is absent.
   */
  recordLabel: z.string().nullable().optional(),
  evidence: z.array(alertEvidenceItemSchema).optional(),
  /**
   * Ids of the call criteria the rule's condition read (#1800) — no quote:
   * the card asks `GET /alerts/:id/criterion-evidence` for what to show.
   * Optional, absent when empty: an older UI ignores it, an older edge-api
   * never sends it.
   */
  criterionEvidenceIds: z.array(z.string()).optional(),
  /**
   * The alert stands on the attention flag's reason (#2002): the card may ask
   * `GET /alerts/:id/attention-moment` for the moment behind it. Present only
   * as `true`; an older UI ignores it, an older edge-api never sends it.
   */
  attentionReasonEvidence: z.literal(true).optional(),
  agentId: z.string().optional().nullable(),
  agentName: z.string().optional().nullable(),
  crmResponsibleId: z.string().optional().nullable(),
  canScheduleCall: z.boolean().optional(),
  crmActionState: z.enum(['pending', 'succeeded', 'failed']).optional().nullable(),
  crmActionType: z.enum(['todo', 'scheduled_call']).optional().nullable(),
  crmExternalActivityId: z.string().optional().nullable(),
  crmActionDeadline: z.string().optional().nullable(),
  crmActionAssigneeId: z.string().optional().nullable(),
  crmActionError: z.string().optional().nullable(),
  triggeredAt: z.string().optional(),
  acknowledgedAt: z.string().optional().nullable(),
  resolvedAt: z.string().optional().nullable(),
  createdAt: z.string().optional(),
  /** True if this alert was triggered by a demo record. Defaults to false. */
  isDemo: z.boolean().default(false),
  /**
   * Whether the caller may ACT on this alert — acknowledge, resolve, the CRM
   * action. Sent only to a managed subject (an access grant, E1.5 #1336),
   * whose list and detail are read by the grant while an action is role ∧
   * grant: the row is read, the action may still be refused. Absent for
   * everyone else — there the read and the action share one boundary (the
   * role), so every row shown is actionable.
   */
  canAct: z.boolean().optional(),
});

/**
 * Incident (user-created from alert/analysis)
 */
export const incidentSchema = z.object({
  id: z.string(),
  analysisId: z.string(),
  recordId: z.string(),
  projectId: z.string(),
  createdByUserId: z.string(),
  severity: alertSeveritySchema,
  status: incidentStatusSchema,
  title: z.string(),
  description: z.string().optional().nullable(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  resolvedAt: z.string().optional().nullable(),
});

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * The rule condition, declared ONCE.
 *
 * It used to exist three times: POST's schema in edge-api, PATCH's schema
 * below, and `createAlertRuleRequestSchema` — the last of them a bare
 * `z.unknown()` with no refine at all, exported from the SSOT package for the
 * next caller to pick up. The duplication had already produced a real
 * divergence (see `alertThrottleSecondsSchema`), so the fragments are shared
 * rather than restated.
 *
 * The refine is not cosmetic: `jsonLogic.apply('x', data)` returns the string
 * itself, which is truthy, so a bare string condition matches EVERY record.
 */
export const alertRuleJsonSchema = z
  .unknown()
  .refine((val) => typeof val === 'object' && val !== null && !Array.isArray(val), {
    message: 'Invalid JSONLogic rule format',
  })
  // An empty condition never matches (the engine skips it — `jsonLogic.apply({})`
  // is truthy and used to match every record), so a rule saved with one would
  // be silently dead (`/code-review` on #1823).
  .refine(
    (val) => typeof val !== 'object' || val === null || Object.keys(val).length > 0,
    {
      message: 'Rule condition is empty',
    }
  );

/**
 * Throttle window, declared ONCE.
 *
 * The upper bound is one year. PATCH used to omit it while POST enforced it,
 * so a rule could be created within the limit and then edited past it — the
 * concrete consequence of having three declarations (Codex P2).
 */
export const alertThrottleSecondsSchema = z.number().int().min(0).max(31_536_000);

export const createAlertRuleRequestSchema = z
  .object({
    projectId: z.string().uuid(),
    name: z.string().min(1).max(255),
    description: z.string().max(1000).optional(),
    category: alertCategorySchema,
    severity: alertSeveritySchema,
    ruleJson: alertRuleJsonSchema,
    isEnabled: z.boolean().default(true),
    presetKey: z.string().min(1).max(100).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    dedupKeyTemplate: z.string().max(500).optional(),
    throttleSeconds: alertThrottleSecondsSchema.optional(),
  })
  .strict();

export const updateAlertRuleRequestSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(1000).optional().nullable(),
    category: alertCategorySchema.optional(),
    severity: alertSeveritySchema.optional(),
    // `.optional()` sits outside the shared fragment so an omitted ruleJson
    // (not being updated) still skips the refine.
    ruleJson: alertRuleJsonSchema.optional(),
    isEnabled: z.boolean().optional(),
    throttleSeconds: alertThrottleSecondsSchema.optional(),
    // When disabling a rule (isEnabled: false), set true to also resolve every
    // still-active alert event triggered by this rule. Ignored when not
    // disabling.
    cleanupActiveAlerts: z.boolean().optional(),
  })
  .strict();

/**
 * Body of the internal POST /api/v1/alerts/backfill (edge-api → workflows-api,
 * after a rule is created with `applyToExisting`).
 *
 * Ids only (#1809): the backfill loads the STORED rule by `ruleId`, scoped to
 * `projectId` and `orgId`, and evaluates exactly that — what the live step
 * reads — instead of rebuilding it from fields the edge copies into the body
 * (the copies had already drifted: metadata, dedup template). Id formats are
 * the lax UUID shape the route has always accepted; the edge creates rule ids
 * as `rule_<uuid>`.
 *
 * Not `.strict()`: an edge from before this change still sends the rule's own
 * fields along, and a workflows-api rolled out first must keep accepting it.
 * The parse drops them — the stored rule is the only source of the condition.
 *
 * The other direction of the same rollout: deploy-production ships edge-api
 * BEFORE workflows-api, and the workflows-api still serving the old release
 * answers 400 «Missing required fields» to a body without `ruleJson`, `severity`,
 * `category`, `ruleName`. So for ONE release the edge sends `AlertBackfillLegacyFields`
 * next to the ids (the new workflows-api ignores them).
 */
export const alertBackfillRequestSchema = z.object({
  ruleId: z
    .string()
    .regex(
      /^rule_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      'ruleId must be rule_<uuid>'
    ),
  projectId: z.guid(),
  orgId: z.guid(),
});

/**
 * The rule's own fields the OLD workflows-api requires in the backfill body.
 * Not read by the new one; never put into `alertBackfillRequestSchema`, which
 * would validate values nobody uses.
 *
 * удалить после выпуска, в котором workflows-api читает правило по id
 */
export interface AlertBackfillLegacyFields {
  /** The condition as the create body validated it (a JSONLogic object). */
  ruleJson: unknown;
  severity: string;
  category: string;
  ruleName: string;
}

/**
 * Body for POST /api/v1/alerts/bulk-resolve.
 * All filters are optional and AND-combined. Without filters every active alert
 * in the org gets resolved.
 */
export const bulkResolveAlertsRequestSchema = z
  .object({
    ruleId: z.string().uuid().optional(),
    projectId: z.string().uuid().optional(),
    severity: alertSeveritySchema.optional(),
    status: alertStatusSchema.optional(),
  })
  .strict();

export const createIncidentRequestSchema = z
  .object({
    analysisId: z.string().uuid(),
    recordId: z.string().uuid(),
    projectId: z.string().uuid(),
    severity: alertSeveritySchema,
    title: z.string().min(1).max(255),
    description: z.string().max(2000).optional(),
  })
  .strict();

export const updateIncidentRequestSchema = z
  .object({
    status: incidentStatusSchema.optional(),
    severity: alertSeveritySchema.optional(),
    title: z.string().min(1).max(255).optional(),
    description: z.string().max(2000).optional().nullable(),
  })
  .strict();

// ============================================================================
// Response Schemas
// ============================================================================

export const alertRulesListResponseSchema = z.object({
  success: z.boolean().optional(),
  items: z.array(alertRuleSchema),
  total: z.number().optional(),
});

export const alertEventsListResponseSchema = z.object({
  success: z.boolean().optional(),
  events: z.array(alertEventSchema),
  total: z.number().optional(),
  limit: z.number().int().optional(),
  offset: z.number().int().optional(),
  /**
   * How many alerts `POST /alerts/bulk-resolve` with the same filters would
   * close (its `status` defaulting to `active`). Sent only to a managed
   * subject — `total` is what its grant reads, the sweep closes only what
   * its role AND its grant admit (see `canAct`). Absent for everyone else:
   * there the two are the same set.
   */
  resolvableTotal: z.number().int().nonnegative().optional(),
});

export const incidentsListResponseSchema = z.object({
  success: z.boolean().optional(),
  incidents: z.array(incidentSchema),
  total: z.number().optional(),
});

export const alertRuleResponseSchema = z.object({
  rule: alertRuleSchema,
  // Set when isEnabled toggled false with cleanupActiveAlerts=true.
  resolvedCount: z.number().int().nonnegative().optional(),
});

export const bulkResolveAlertsResponseSchema = z.object({
  resolvedCount: z.number().int().nonnegative(),
});

export const incidentResponseSchema = z.object({
  incident: incidentSchema,
});

// ============================================================================
// Type exports
// ============================================================================

export type AlertSeverity = z.infer<typeof alertSeveritySchema>;
export type AlertStatus = z.infer<typeof alertStatusSchema>;
export type AlertCategory = z.infer<typeof alertCategorySchema>;
export type IncidentStatus = z.infer<typeof incidentStatusSchema>;
export type AlertRule = z.infer<typeof alertRuleSchema>;
export type AlertEvidenceItem = z.infer<typeof alertEvidenceItemSchema>;
export type AlertEvent = z.infer<typeof alertEventSchema>;
export type AlertCriterionEvidence = z.infer<typeof alertCriterionEvidenceSchema>;
export type AlertCriterionEvidenceResponse = z.infer<
  typeof alertCriterionEvidenceResponseSchema
>;
export type AlertAttentionMoment = z.infer<typeof alertAttentionMomentSchema>;
export type AlertAttentionMomentResponse = z.infer<
  typeof alertAttentionMomentResponseSchema
>;
export type Incident = z.infer<typeof incidentSchema>;
export type CreateAlertRuleRequest = z.infer<typeof createAlertRuleRequestSchema>;
export type UpdateAlertRuleRequest = z.infer<typeof updateAlertRuleRequestSchema>;
export type CreateIncidentRequest = z.infer<typeof createIncidentRequestSchema>;
export type UpdateIncidentRequest = z.infer<typeof updateIncidentRequestSchema>;
export type AlertRulesListResponse = z.infer<typeof alertRulesListResponseSchema>;
export type AlertEventsListResponse = z.infer<typeof alertEventsListResponseSchema>;
export type IncidentsListResponse = z.infer<typeof incidentsListResponseSchema>;
export type AlertBackfillRequest = z.infer<typeof alertBackfillRequestSchema>;
export type BulkResolveAlertsRequest = z.infer<typeof bulkResolveAlertsRequestSchema>;
export type BulkResolveAlertsResponse = z.infer<typeof bulkResolveAlertsResponseSchema>;
